// @flow
import React, { Component } from 'react'
import styled, { type StyledComponent } from 'styled-components'
import _ from 'lodash'
import FloatPanel from './controls/FloatPanel'
import PrimaryButton from './controls/PrimaryButton'
import Button from './controls/SmallRoundedButton'
import type { OptionType } from './types'
import ErrorVibesLink from './controls/ErrorVibesLink'
import YandexBlock from './StatisticMetricsPage/YandexBlock'
import GoogleAnalyticsBlock from './StatisticMetricsPage/GoogleAnalyticsBlock'
import { translations, translateFunc } from '../shared/localize'

const EmptyMessageWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-top: 15px;
  padding-bottom: 15px;
`
const ButtonWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-left: 30px;
`
const SmallButtonWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-top: 10px;
  padding-bottom: 10px;
`
export const BlockWrapper: StyledComponent<Object, any, any> = styled.div`
  width: 480px;
  padding-top: 10px;
`

type Goal = {|
  id: string,
  name: string
|}

export type SelectedCounterWithGoals = {|
  counterId: string,
  provider: string,
  goalIds: Array<number>
|}

export type Counter = {|
  id: string,
  name: string,
  webPropertyId: string,
  accountId: string,
  goals: Array<Goal>,
  provider: string
|}

type WebAnalyticsProps = {|
  updateTokenPath: string,
  isTokenPresent: boolean,
  isTokenDeactivated: boolean
|}

type Props = {|
  initialAllCounters: Array<Counter>,
  initialSelectedCountersWithGoals: Array<SelectedCounterWithGoals>,
  allCounters: Array<Counter>,
  selectedYandexCounterId: string,
  selectedGACounterIds: Array<string>,
  selectedYandexGoalIds: Array<string>,
  selectedGAGoalIds: Array<string>,
  isLoading: boolean,
  yandexMetricaProps: WebAnalyticsProps,
  googleAnalyticsProps: WebAnalyticsProps,
  gaDataType: 'goals' | 'transactions',
  gaAccounts: Array<OptionType>,
  selectedGAAccountId: string,
  onInit: Function,
  onChange: Function,
  onCountersUpdate: Function,
  onApply: Function
|}

const t = translateFunc(translations.statistic_metrics_page)

export default class StatisticMetricsPage extends Component<Props> {
  constructor(props: Props) {
    super(props)

    const selectedYandexCounterId = this.selectedYandexCounterId()
    const selectedGAAccountId = this.selectedGAAccountId()
    const selectedGACounterIds = this.selectedGACounterIds(selectedGAAccountId)

    props.onInit({
      allCounters: props.initialAllCounters,
      selectedYandexCounterId: selectedYandexCounterId,
      selectedGAAccountId: selectedGAAccountId,
      selectedGACounterIds: selectedGACounterIds,
      selectedYandexGoalIds: this.isTokenActive(props.yandexMetricaProps)
        ? this.selectedGoalIds([selectedYandexCounterId], 'metrica')
        : [],
      selectedGAGoalIds: this.isTokenActive(props.googleAnalyticsProps)
        ? this.selectedGoalIds(selectedGACounterIds, 'google_analytics')
        : []
    })
  }

  render() {
    const { yandexMetricaProps, googleAnalyticsProps } = this.props
    return (
      <div>
        <div>
          <div className="top-info">
            <h3>{t('metrica')}</h3>
            <SmallButtonWrapper>
              {this.renderTopButton(yandexMetricaProps, 'metrica')}
            </SmallButtonWrapper>
          </div>
          {this.isTokenActive(yandexMetricaProps) && (
            <YandexBlock
              allCounters={this.props.allCounters}
              counterOptions={this.metricaCounterOptions()}
              selectedCounterId={this.props.selectedYandexCounterId}
              selectedGoalIds={this.props.selectedYandexGoalIds}
              isLoading={this.props.isLoading}
              onChange={(diff) => this.props.onChange(diff)}
            />
          )}
        </div>
        <div>
          <div className="top-info">
            <h3>{t('google_analytics')}</h3>
            <SmallButtonWrapper>
              {this.renderTopButton(googleAnalyticsProps, 'google_analytics')}
            </SmallButtonWrapper>
          </div>
          {this.isTokenActive(googleAnalyticsProps) && (
            <GoogleAnalyticsBlock
              allCounters={this.props.allCounters}
              accountOptions={this.props.gaAccounts}
              selectedAccountId={this.props.selectedGAAccountId}
              counterOptions={this.gaCounterOptions()}
              selectedCounterIds={this.props.selectedGACounterIds}
              selectedGoalIds={this.props.selectedGAGoalIds}
              isLoading={this.props.isLoading}
              dataType={this.props.gaDataType}
              onChange={(diff) => this.props.onChange(diff)}
            />
          )}
        </div>
        {(this.isTokenActive(yandexMetricaProps) || this.isTokenActive(googleAnalyticsProps)) &&
          this.renderFooter()}
      </div>
    )
  }

  renderTopButton(webAnalyticsProps: WebAnalyticsProps, provider: string) {
    const { updateTokenPath, isTokenDeactivated } = webAnalyticsProps
    if (this.isTokenActive(webAnalyticsProps)) {
      return (
        <Button
          disabled={this.props.isLoading}
          onClick={() => this.props.onCountersUpdate(provider)}
        >
          {t('update')}
        </Button>
      )
    } else if (isTokenDeactivated) {
      return (
        <ErrorVibesLink className="small-blue-button" href={updateTokenPath}>
          {t('refresh_token')}
        </ErrorVibesLink>
      )
    } else {
      return (
        <a className="small-blue-button" href={updateTokenPath}>
          {t('integrate')}
        </a>
      )
    }
  }

  isTokenActive(webAnalyticsProps: WebAnalyticsProps): boolean {
    const { isTokenPresent, isTokenDeactivated } = webAnalyticsProps
    return isTokenPresent && !isTokenDeactivated
  }

  renderFooter() {
    return (
      <FloatPanel>
        <ButtonWrapper>
          <PrimaryButton
            height={32}
            disabled={this.props.isLoading}
            onClick={() => this.props.onApply()}
          >
            {t('apply')}
          </PrimaryButton>
        </ButtonWrapper>
      </FloatPanel>
    )
  }

  selectedYandexCounterId(): string {
    const { initialAllCounters, initialSelectedCountersWithGoals, yandexMetricaProps } = this.props
    if (!this.isTokenActive(yandexMetricaProps)) return ''

    const initialCounter = _.find(initialSelectedCountersWithGoals, (counter) => {
      return _.isEqual(counter.provider, 'metrica')
    })
    if (initialCounter) return initialCounter.counterId

    const counter = _.find(initialAllCounters, (counter) => {
      return _.isEqual(counter.provider, 'metrica')
    })
    return counter ? counter.id : ''
  }

  selectedGAAccountId(): string {
    const {
      initialAllCounters,
      initialSelectedCountersWithGoals,
      googleAnalyticsProps
    } = this.props
    if (!this.isTokenActive(googleAnalyticsProps)) return ''

    const selectedCounter = _.find(initialSelectedCountersWithGoals, (counter) => {
      return _.isEqual(counter.provider, 'google_analytics')
    })
    const gaCounters = _.filter(initialAllCounters, (counter) => {
      return _.isEqual(counter.provider, 'google_analytics')
    })
    if (selectedCounter === void 0) {
      return _.first(gaCounters) ? _.first(gaCounters).accountId : ''
    } else {
      const counter = _.find(gaCounters, (counter) => {
        return _.isEqual(counter.id, selectedCounter.counterId)
      })
      return counter ? counter.accountId : ''
    }
  }

  selectedGACounterIds(selectedGAAccountId: string): Array<string> {
    const {
      initialAllCounters,
      initialSelectedCountersWithGoals,
      googleAnalyticsProps
    } = this.props
    if (!this.isTokenActive(googleAnalyticsProps)) return []

    const counterIds = _.map(
      _.filter(initialSelectedCountersWithGoals, (counter) => {
        return _.isEqual(counter.provider, 'google_analytics')
      }),
      'counterId'
    )
    if (!_.isEmpty(counterIds)) return counterIds

    const counter = _.find(initialAllCounters, (counter) => {
      return (
        _.isEqual(counter.provider, 'google_analytics') &&
        _.isEqual(counter.accountId, selectedGAAccountId)
      )
    })
    return counter ? [counter.id] : []
  }

  selectedGoalIds(counterIds: Array<string>, provider: string): Array<string> {
    const { initialSelectedCountersWithGoals } = this.props
    return _.flatMap(
      _.filter(initialSelectedCountersWithGoals, (counter) => {
        return _.isEqual(counter.provider, provider) && _.includes(counterIds, counter.counterId)
      }),
      'goalIds'
    )
  }

  metricaCounterOptions(): Array<OptionType> {
    const { allCounters } = this.props
    return _.map(
      _.filter(allCounters, (counter) => {
        return _.isEqual(counter.provider, 'metrica')
      }),
      (counter) => {
        return { id: counter.id, name: counter.name }
      }
    )
  }

  gaCounterOptions(): Array<OptionType> {
    const { allCounters, selectedGAAccountId } = this.props
    return _.map(
      _.filter(allCounters, (counter) => {
        return (
          _.isEqual(counter.provider, 'google_analytics') &&
          _.isEqual(counter.accountId, selectedGAAccountId)
        )
      }),
      (counter) => {
        return { id: counter.id, name: `[${counter.webPropertyId}] ${counter.name}` }
      }
    )
  }
}

export function renderEmptyMessage() {
  return <EmptyMessageWrapper>{t('empty_message')}</EmptyMessageWrapper>
}
